import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { Loader2, MailCheck, Users } from "lucide-react";
import { toast } from "sonner";

import { GET_FAMILY_INVITATION } from "@/graphql/queries/family";
import { ACCEPT_FAMILY_INVITATION } from "@/graphql/mutations/family";
import { useAuth } from "@/lib/auth/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ChildDataConsent,
  TermsConsent,
} from "@/components/legal/terms-consent";
import { GuardianConfirmation } from "@/components/legal/guardian-confirmation";
import { LegalFooter } from "@/components/legal/legal-footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Where an invitation link lands.
 *
 * Reachable signed out on purpose: the person opening it may have no account
 * here, and refusing to say anything until they sign in asks them to
 * authenticate into a thing nobody has named. So the page first says who invited
 * them and to what, and only then asks them to sign in.
 *
 * The token is remembered across the Google round trip (`sessionStorage`), which
 * is the whole reason that detour is survivable — OAuth returns to `/`, and
 * without this the invitation would be a link that works only for somebody
 * already logged in.
 */
const PENDING_KEY = "scio:pending-invitation";

export function rememberPendingInvitation(token: string) {
  try {
    sessionStorage.setItem(PENDING_KEY, token);
  } catch {
    /* Private browsing. The link still works once signed in. */
  }
}

export function takePendingInvitation(): string | null {
  try {
    const token = sessionStorage.getItem(PENDING_KEY);
    if (token) sessionStorage.removeItem(PENDING_KEY);
    return token;
  } catch {
    return null;
  }
}

export default function AcceptInvitationPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { authenticated, isGuardian, loading: authLoading } = useAuth();

  const { data, loading } = useQuery(GET_FAMILY_INVITATION, {
    variables: { token },
    fetchPolicy: "cache-and-network",
  });
  const [accept] = useMutation(ACCEPT_FAMILY_INVITATION);
  const [joining, setJoining] = useState(false);
  // The Google button here can create an account — the invited person may have
  // none — so it carries the same confirmations the register page asks for
  // (issue #55: only a grown-up registers). For an account that already exists
  // the server ignores them.
  const [agreed, setAgreed] = useState(false);
  const [childData, setChildData] = useState(false);
  const [confirmsGuardian, setConfirmsGuardian] = useState(false);
  const ready = agreed && childData && confirmsGuardian;

  const invitation = (data as any)?.familyInvitation ?? null;

  // Stashed on arrival rather than at the moment they press "Prisijungti su
  // Google": people arrive here from a mail client, and some will use the
  // browser's own back-and-forth rather than the button.
  //
  // Only while signed out, which is the whole job — surviving the sign-in
  // detour. Storing it once signed in would leave a token behind that drags the
  // account back to this page on every later visit.
  useEffect(() => {
    if (token && !authLoading && !authenticated)
      rememberPendingInvitation(token);
  }, [token, authLoading, authenticated]);

  const join = async () => {
    setJoining(true);
    try {
      const result: any = await accept({ variables: { token } });
      const errors = result?.data?.acceptFamilyInvitation?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      takePendingInvitation();
      toast.success("Prisijungėte prie šeimos");
      navigate("/family", { replace: true });
    } catch (error) {
      toast.error("Nepavyko prisijungti");
      console.error(error);
    } finally {
      setJoining(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
        <LegalFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Kvietimas į šeimą
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!invitation ? (
              <>
                <p className="text-muted-foreground">
                  Tokio kvietimo nėra. Nuoroda gali būti nukopijuota ne visa
                  arba kvietimas jau atšauktas.
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">Į pradžią</Link>
                </Button>
              </>
            ) : (
              <>
                <p>
                  {invitation.invitedByName ? (
                    <>
                      <strong>{invitation.invitedByName}</strong> kviečia jus
                      prisijungti prie šeimos
                    </>
                  ) : (
                    <>Jus kviečia prisijungti prie šeimos</>
                  )}{" "}
                  {invitation.familyName ? (
                    <strong>„{invitation.familyName}"</strong>
                  ) : null}
                  .
                </p>
                <p className="text-sm text-muted-foreground">
                  Prisijungę matysite tų pačių vaikų sesijas, pažangą ir
                  ataskaitas. Prenumeratos pirkti nereikia — ji lieka šeimos.
                </p>

                {/* One sentence, from the server, whatever the reason: expired,
                  already used, wrong kind of account, or a household of their
                  own they would have to leave first. Computing it here would be
                  a second copy of `FamilyMembership` that the mutation could
                  then contradict. */}
                {invitation.refusal &&
                  !(!authenticated && !invitation.open) && (
                    <p className="rounded-md bg-muted px-3 py-2 text-sm">
                      {invitation.refusal}
                    </p>
                  )}

                {!authenticated ? (
                  <div className="space-y-3">
                    <GuardianConfirmation
                      checked={confirmsGuardian}
                      onChange={setConfirmsGuardian}
                    />
                    <TermsConsent checked={agreed} onChange={setAgreed} />
                    <ChildDataConsent
                      checked={childData}
                      onChange={setChildData}
                    />
                    <Button
                      asChild
                      className={`w-full ${ready ? "" : "pointer-events-none opacity-50"}`}
                    >
                      <a
                        href={
                          ready
                            ? "/users/auth/google_oauth2?role=guardian&terms=1&child_data=1&guardian=1"
                            : undefined
                        }
                        aria-disabled={!ready}
                        tabIndex={ready ? undefined : -1}
                      >
                        <MailCheck className="mr-2 h-4 w-4" />
                        Prisijungti su Google
                      </a>
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      Kvietimas išsiųstas adresu {invitation.email}
                    </p>
                  </div>
                ) : (
                  <Button
                    className="w-full"
                    disabled={joining || !invitation.open || !isGuardian}
                    onClick={join}
                  >
                    {joining && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Prisijungti prie šeimos
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <LegalFooter />
    </div>
  );
}
